import React, { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useEmpMedicoesMutations } from '@/hooks/useEmpMedicoes';

/**
 * SLICE F3 — Criação mínima de Boletim de Medição (sentinela).
 *
 * Espelha CriarOSMinimoDialog. Apenas validação de formato no frontend
 * (datas presentes, fim ≥ início). Toda regra real fica no backend
 * (RPC emp_criar_boletim_minimo + triggers Safety Layer v2).
 *
 * Erro do backend é exibido bruto (error.message) — sem tradução, sem
 * lógica de negócio no frontend.
 */
const schema = z
  .object({
    periodo_inicio: z.string().min(1, 'Informe a data de início'),
    periodo_fim: z.string().min(1, 'Informe a data de fim'),
  })
  .refine((v) => v.periodo_fim >= v.periodo_inicio, {
    message: 'Fim do período deve ser ≥ início',
    path: ['periodo_fim'],
  });

interface Props {
  contratoId: string;
  /** Versão de planilha vigente (opcional — backend resolve se omitido). */
  planilhaVersaoId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (boletimId: string) => void;
}

export const CriarBoletimMinimoDialog: React.FC<Props> = ({
  contratoId,
  planilhaVersaoId,
  open,
  onOpenChange,
  onCreated,
}) => {
  const { criarBoletimMinimo } = useEmpMedicoesMutations(contratoId);
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFim, setPeriodoFim] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setPeriodoInicio('');
      setPeriodoFim('');
      setErro(null);
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const submit = () => {
    const parsed = schema.safeParse({
      periodo_inicio: periodoInicio,
      periodo_fim: periodoFim,
    });
    if (!parsed.success) {
      setErro(parsed.error.issues[0]?.message ?? 'Inválido');
      return;
    }
    setErro(null);
    criarBoletimMinimo.mutate(
      {
        periodo_inicio: parsed.data.periodo_inicio,
        periodo_fim: parsed.data.periodo_fim,
        planilha_versao_id: planilhaVersaoId ?? null,
      },
      {
        onSuccess: (boletimId) => {
          onOpenChange(false);
          onCreated?.(boletimId);
        },
        onError: (e: any) => {
          // Mensagem bruta do backend, conforme escopo do slice.
          setErro(e?.message ?? 'Erro desconhecido');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Novo Boletim de Medição</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="space-y-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="bm-inicio">Início do período</Label>
              <Input
                id="bm-inicio"
                ref={inputRef}
                type="date"
                value={periodoInicio}
                onChange={(e) => setPeriodoInicio(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bm-fim">Fim do período</Label>
              <Input
                id="bm-fim"
                type="date"
                value={periodoFim}
                onChange={(e) => setPeriodoFim(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>
          <p
            className={`text-xs ${erro ? 'text-destructive' : 'text-muted-foreground'}`}
          >
            {erro ??
              'Boletim será criado como rascunho sentinela. Itens e demais campos podem ser preenchidos depois.'}
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={criarBoletimMinimo.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={criarBoletimMinimo.isPending}>
              {criarBoletimMinimo.isPending ? 'Criando…' : 'Criar Boletim'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
